import { GridApi, SortModelItem } from '@ag-grid-community/core';
import { useCallback, useRef } from 'react';
import { sortModelFromColumnState } from '@/components/ui/responsive-grid/sortModelFromColumnState';
import { usePersistedState } from '@/hooks/usePersistedState';

export type PersistedGridState = {
    filterModel: Record<string, unknown>;
    sortModel: SortModelItem[];
};

export const EMPTY_GRID_STATE: PersistedGridState = {
    filterModel: {},
    sortModel: [],
};

export function applyPersistedGridState(api: GridApi, state: PersistedGridState) {
    api.setFilterModel(state.filterModel ?? {});
    api.applyColumnState({
        state: (state.sortModel ?? []).map((item, index) => ({
            colId: item.colId,
            sort: item.sort,
            sortIndex: index,
        })),
        defaultState: { sort: null },
    });
}

export function usePersistedGridState<T extends PersistedGridState>(key: string, defaultValue: T) {
    const [gridState, setGridState] = usePersistedState(key, defaultValue);
    const applyingRef = useRef(false);

    const saveGridState = useCallback(
        (api: GridApi) => {
            if (applyingRef.current) {
                return;
            }
            setGridState((prev) => ({
                ...prev,
                filterModel: api.getFilterModel() ?? {},
                sortModel: sortModelFromColumnState(api.getColumnState()),
            }));
        },
        [setGridState],
    );

    const applyGridState = useCallback(
        (api: GridApi, state: PersistedGridState = gridState) => {
            applyingRef.current = true;
            try {
                applyPersistedGridState(api, state);
            } finally {
                applyingRef.current = false;
            }
        },
        [gridState],
    );

    return { gridState, setGridState, saveGridState, applyGridState };
}
